import api, { handleResponse, handleError } from './api';

const friendService = {
  /**
   * Get current user's friends
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async getFriends() {
    try {
      const response = await api.get('/friends');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get pending friend requests received by current user
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async getPendingRequests() {
    try {
      const response = await api.get('/friends/requests/pending');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get friend requests sent by current user
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async getSentRequests() {
    try {
      const response = await api.get('/friends/requests/sent');
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Send a friend request
   * @param {string} receiverId - User ID to send request to
   * @param {string} message - Optional message with the request
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async sendFriendRequest(receiverId, message = null) {
    try {
      const response = await api.post('/friends/requests', { receiverId, message });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Respond to a friend request
   * @param {string} requestId - Friend request ID
   * @param {boolean} accept - Whether to accept or decline
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async respondToRequest(requestId, accept) {
    try {
      const response = await api.post(`/friends/requests/${requestId}/respond`, { accept });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Cancel a sent friend request
   * @param {string} requestId - Friend request ID to cancel
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async cancelRequest(requestId) {
    try {
      const response = await api.delete(`/friends/requests/${requestId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Remove a friend
   * @param {string} friendUserId - User ID of the friend to remove
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async removeFriend(friendUserId) {
    try {
      const response = await api.delete(`/friends/${friendUserId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get friendship status with another user
   * @param {string} otherUserId - User ID to check status with
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async getFriendshipStatus(otherUserId) {
    try {
      const response = await api.get(`/friends/status/${otherUserId}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Search for people to add as friends
   * @param {string} query - Search query
   * @param {number} limit - Max results to return
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async searchPeople(query, limit = 20) {
    try {
      const response = await api.get(`/friends/search?q=${encodeURIComponent(query)}&limit=${limit}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Get suggested friends (people you may know)
   * @param {number} limit - Max results to return
   * @returns {Promise<{success: boolean, data: object[]|null, error: string|null}>}
   */
  async getSuggestedFriends(limit = 10) {
    try {
      const response = await api.get(`/friends/suggestions?limit=${limit}`);
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
};

export default friendService;
